import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Button,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  Refresh,
  ArrowForward,
  MoreHoriz,
  AirplanemodeActive
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';
import { isSpectator } from '../../utils/rolePermissions';
import ClientAircraftDetail from './ClientAircraftDetail';
import {
  KPI_THEMES,
  COLORS,
  accentBarSx,
  pillSecondarySx,
  pillPrimarySx,
  alertErrorSx,
  alertInfoSx,
  panelShellSx,
  surfaceInsetSx,
  getAircraftGlassCardSx,
  getAircraftStatusAccent,
  stackedCardWrapSx
} from './clientPortalTheme';

const KPI_KEYS = ['available', 'inFlight', 'maintenance', 'grounded', 'inspection', 'activeCharters'];

const KpiPill = ({ title, value, accent, bg }) => (
  <Box
    sx={{
      height: '100%',
      width: '100%',
      borderRadius: 999,
      px: 2,
      py: 1.15,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1.25,
      bgcolor: bg,
      background: `linear-gradient(135deg, ${bg} 0%, rgba(255,255,255,0.85) 100%)`,
      border: `1.5px solid ${accent}70`,
      boxShadow: `0 4px 16px ${accent}18, inset 0 1px 0 rgba(255,255,255,0.8)`,
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 22px ${accent}28, inset 0 1px 0 rgba(255,255,255,0.95)`
      }
    }}
  >
    <Typography
      variant="caption"
      sx={{
        color: accent,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        fontSize: '0.68rem'
      }}
    >
      {title}
    </Typography>
    <Typography variant="h6" fontWeight={800} sx={{ color: accent, lineHeight: 1 }}>
      {value}
    </Typography>
  </Box>
);

const formatDate = (value) => {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const AircraftThumb = ({ accent, registration }) => (
  <Box
    sx={{
      mt: 1.5,
      height: 72,
      borderRadius: 2,
      overflow: 'hidden',
      position: 'relative',
      border: `1.5px solid ${accent}55`,
      background: `
        linear-gradient(135deg, ${accent}22 0%, rgba(245,240,232,0.95) 55%, rgba(255,253,249,0.92) 100%),
        radial-gradient(ellipse at 70% 40%, ${accent}28 0%, transparent 55%)
      `,
      boxShadow: `inset 0 0 20px ${accent}12`
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0.55
      }}
    >
      <AirplanemodeActive sx={{ fontSize: 40, color: accent, transform: 'rotate(-12deg)' }} />
    </Box>
    <Typography
      variant="caption"
      sx={{
        position: 'absolute',
        bottom: 6,
        left: 10,
        color: COLORS.textSecondary,
        fontWeight: 700,
        letterSpacing: 0.6,
        fontSize: '0.65rem',
        textTransform: 'uppercase'
      }}
    >
      {registration}
    </Typography>
  </Box>
);

const ClientFleetDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const detailRef = useRef(null);
  const [dashboard, setDashboard] = useState(null);
  const [selectedAircraftId, setSelectedAircraftId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/client-portal/dashboard');
      setDashboard(response.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Failed to load dashboard. Please try again.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    const interval = setInterval(() => fetchDashboard(true), 60000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  const handleSelectAircraft = (aircraftId) => {
    setSelectedAircraftId(aircraftId);
    setTimeout(() => {
      detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={360}>
        <CircularProgress sx={{ color: COLORS.orange }} />
      </Box>
    );
  }

  const kpis = dashboard?.kpis || {};
  const summaries = dashboard?.aircraftSummaries || [];
  const isFleetWideView = [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(user?.role) || isSpectator(user?.role);

  return (
    <Box>
      <Box sx={{ ...panelShellSx, p: { xs: 2, md: 3 }, mb: 2.5 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={2}
          mb={2.5}
        >
          <Box>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                color: COLORS.orange,
                letterSpacing: '-0.02em',
                textShadow: 'none',
                lineHeight: 1.15
              }}
            >
              Welcome, {user?.firstName || (isFleetWideView ? 'Admin' : 'Client')}
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.textSecondary, mt: 0.75, maxWidth: 520 }}>
              {isFleetWideView
                ? 'Your fleet status across the client portal.'
                : 'Your fleet at a glance.'}
              {dashboard?.lastUpdated && ` · Updated ${new Date(dashboard.lastUpdated).toLocaleTimeString()}`}
            </Typography>
          </Box>

          <Box display="flex" gap={1.25} flexWrap="wrap" alignItems="center">
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={() => fetchDashboard()}
              sx={pillSecondarySx}
            >
              Refresh
            </Button>
            {isFleetWideView && (
              <Button
                size="small"
                onClick={() => navigate('/aircraft')}
                sx={pillPrimarySx}
              >
                Manage Aircraft
              </Button>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, ...alertErrorSx }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={1.25} sx={{ mb: summaries.length ? 3 : 0 }}>
          {KPI_KEYS.map((key, idx) => (
            <Grid item xs={6} sm={4} md={2} key={key} sx={{ display: 'flex' }}>
              <KpiPill
                title={KPI_THEMES[idx].label}
                value={kpis[key] || 0}
                accent={KPI_THEMES[idx].accent}
                bg={KPI_THEMES[idx].bg}
              />
            </Grid>
          ))}
        </Grid>

        {summaries.length === 0 ? (
          <Alert severity="info" sx={{ ...alertInfoSx }}>
            {isFleetWideView
              ? 'No active aircraft found for this organization.'
              : 'No aircraft have been assigned to your account yet. Please contact your administrator.'}
          </Alert>
        ) : (
          <>
            <Box sx={{ ...accentBarSx, mb: 2, alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.orange }}>
                  {isFleetWideView ? 'All Aircraft' : 'Your Aircraft'}
                </Typography>
                <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
                  Tap a card to view full details
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2.5}>
              {summaries.map((summary) => {
                const route = summary.currentSector
                  ? `${summary.currentSector.departure?.airport} → ${summary.currentSector.arrival?.airport}`
                  : null;
                const isSelected = selectedAircraftId === summary._id;
                const accent = getAircraftStatusAccent(summary.status);
                const sectorDate = formatDate(summary.currentSector?.departure?.dateTime || summary.currentSector?.date);

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={summary._id} sx={{ display: 'flex' }}>
                    <Box sx={{ ...stackedCardWrapSx(accent), pb: 1.5, height: '100%', width: '100%' }}>
                      <Card
                        elevation={0}
                        sx={{
                          ...getAircraftGlassCardSx(summary.status),
                          position: 'relative',
                          zIndex: 1,
                          transition: 'transform 0.22s ease, box-shadow 0.22s ease',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            boxShadow: `0 0 0 1px ${accent}30, 0 14px 32px ${accent}22, 0 6px 14px rgba(15,39,68,0.08)`
                          },
                          width: '100%',
                          height: '100%',
                          minHeight: 340,
                          display: 'flex',
                          flexDirection: 'column',
                          outline: isSelected ? `2px solid ${accent}` : 'none',
                          outlineOffset: 2
                        }}
                      >
                        <CardActionArea
                          onClick={() => handleSelectAircraft(summary._id)}
                          sx={{
                            borderRadius: 4,
                            flex: 1,
                            height: '100%',
                            display: 'flex',
                            alignItems: 'stretch',
                            color: COLORS.text
                          }}
                        >
                          <CardContent
                            sx={{
                              p: 1.75,
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              position: 'relative',
                              '&:last-child': { pb: 1.75 }
                            }}
                          >
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.25} flexShrink={0}>
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{
                                  color: accent,
                                  fontWeight: 800,
                                  letterSpacing: 1,
                                  textTransform: 'uppercase',
                                  fontSize: '0.65rem'
                                }}
                              >
                                {summary.status?.replace(/_/g, ' ')}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectAircraft(summary._id);
                                }}
                                sx={{ color: COLORS.textMuted, p: 0.25 }}
                              >
                                <MoreHoriz fontSize="small" />
                              </IconButton>
                            </Box>

                            <Typography
                              variant="h6"
                              fontWeight={800}
                              noWrap
                              sx={{ color: COLORS.text, lineHeight: 1.2, mb: 0.25, flexShrink: 0 }}
                            >
                              {summary.registration}
                            </Typography>
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{ color: COLORS.textMuted, fontWeight: 500, display: 'block', mb: 1.25, flexShrink: 0 }}
                            >
                              {summary.manufacturer} {summary.model}
                            </Typography>

                            <Box sx={{ height: 56, flexShrink: 0, overflow: 'hidden' }}>
                              {route ? (
                                <Box sx={{ p: 1.1, borderRadius: 2, height: '100%', ...surfaceInsetSx, borderLeft: `3px solid ${accent}` }}>
                                  <Typography variant="body2" fontWeight={700} noWrap sx={{ color: COLORS.text, display: 'block' }}>
                                    {route}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: accent,
                                      fontWeight: 600,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 0.25,
                                      mt: 0.35,
                                      fontSize: '0.65rem'
                                    }}
                                  >
                                    Active sector <ArrowForward sx={{ fontSize: 10 }} />
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
                                  No active sector
                                </Typography>
                              )}
                            </Box>

                            <Box sx={{ height: 28, mt: 0.75, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                              {summary.activeCharterNumber ? (
                                <Chip
                                  label={summary.activeCharterNumber}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    borderRadius: 999,
                                    bgcolor: COLORS.orangeGlass,
                                    color: COLORS.orange,
                                    fontWeight: 600,
                                    border: `1px solid ${COLORS.orange}55`
                                  }}
                                />
                              ) : null}
                            </Box>

                            <Box
                              display="flex"
                              justifyContent="space-between"
                              gap={1}
                              mt={1.25}
                              flexShrink={0}
                              sx={{ color: COLORS.textMuted }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                  Date sector
                                </Typography>
                                <Typography variant="caption" fontWeight={700} noWrap sx={{ color: COLORS.textSecondary }}>
                                  {sectorDate || '—'}
                                </Typography>
                              </Box>
                              <Box textAlign="right" sx={{ minWidth: 0 }}>
                                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                  Hours
                                </Typography>
                                <Typography variant="caption" fontWeight={700} noWrap sx={{ color: COLORS.textSecondary }}>
                                  {summary.tsnSnapshot?.periodValue ?? '—'}
                                  {summary.tsnSnapshot?.landings != null ? ` · ${summary.tsnSnapshot.landings} ldg` : ''}
                                </Typography>
                              </Box>
                            </Box>

                            <Box sx={{ height: 26, mt: 1, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                              {summary.openWorkOrderCount > 0 ? (
                                <Chip
                                  label={`${summary.openWorkOrderCount} WO`}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.6rem',
                                    borderRadius: 999,
                                    bgcolor: COLORS.red,
                                    color: COLORS.white,
                                    fontWeight: 600
                                  }}
                                />
                              ) : null}
                            </Box>

                            <Box sx={{ height: 18, mt: 0.5, flexShrink: 0 }}>
                              {summary.nextMaintenanceDue?.dueDate ? (
                                <Typography variant="caption" noWrap display="block" sx={{ color: COLORS.textMuted, fontSize: '0.65rem' }}>
                                  Maint: {formatDate(summary.nextMaintenanceDue.dueDate)}
                                </Typography>
                              ) : null}
                            </Box>

                            <Box sx={{ mt: 'auto', flexShrink: 0 }}>
                              <AircraftThumb accent={accent} registration={summary.registration} />
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </Box>

      <Box ref={detailRef} sx={{ mt: 1 }}>
        {selectedAircraftId && (
          <ClientAircraftDetail
            aircraftId={selectedAircraftId}
            onClose={() => setSelectedAircraftId(null)}
          />
        )}
      </Box>
    </Box>
  );
};

export default ClientFleetDashboard;
